import root from 'window-or-global';
import axios, {AxiosResponse} from 'axios';
import {isAxiosError, isError} from '@epic-mw/error-types';
import qs from 'querystringify';

const getUrl = () => {
    const clientConfig = root.clientEnvConfig || {};
    const EPIC_SERVICE_GREENHOUSE_URL =
        process.env.EPIC_SERVICE_GREENHOUSE_URL || clientConfig.EPIC_SERVICE_GREENHOUSE_URL;
    if (!EPIC_SERVICE_GREENHOUSE_URL) {
        throw new Error('EPIC_SERVICE_GREENHOUSE_URL env config is not set!');
    }
    return EPIC_SERVICE_GREENHOUSE_URL;
};

interface Count {
    [key: string]: number;
}

export interface Counts {
    [key: string]: Count;
}

export interface Questions {
    questions: {
        required: boolean;
        label: string;
        fields: {
            name: string;
            type: string;
            values: {
                value: number;
                label: string;
            }[];
        }[];
    }[];
}

export interface JobProps {
    education: string;
    questions:
        | {
              required: boolean;
              label: string;
              description: string;
              fields: {
                  name: string;
                  type: string;
                  values: {
                      value: number;
                      label: string;
                  }[];
              }[];
          }[]
        | undefined;
    demographic_questions:
        | {
              header: string;
              description: string;
              questions: {
                  id: number;
                  label: string;
                  required: boolean;
                  type: string;
                  answer_options: {
                      id: number;
                      label: string;
                      free_form: boolean;
                  }[];
              };
          }
        | undefined;
    absolute_url: string;
    city: string[];
    company: string;
    content?: string;
    country: string[];
    data_compliance: {
        requires_consent: boolean;
        retention_period?: string;
        type: string;
    }[];
    department: string;
    departments: {
        child_ids: string[];
        id: number;
        name: string;
        parent_id: number;
    }[];
    id: number;
    internal_job_id: number;
    location: {
        name: string;
    };
    metadata: {
        id: number;
        name: string;
        value: string;
        value_type: string;
    }[];
    offices: {
        child_ids: string[];
        id: number;
        location: string;
        name: string;
        parent_id: number;
    }[];
    product: string;
    remote?: boolean;
    requisition_id: string;
    spotlight?: boolean;
    state: string[];
    title: string;
    type: string;
    updated_at: string;
}

export interface JobsRes {
    counts?: Counts;
    hits?: JobProps[];
    total?: number;
    error?: string;
}

export interface EducationsRes {
    items: {
        id: number;
        text: string;
    }[];
    meta: {
        total_count: number;
        per_page: number;
    };
}

export interface ApplicationFormData {}

interface Request {
    [key: string]: string | number | boolean;
}

interface JobError extends Error {
    info?: any;
    status?: number;
}
class JobFetch {
    public async getJobs(requestStr?: string) {
        const request: Request = JSON.parse(requestStr || '{}');
        const queryParams = qs.stringify(request);

        try {
            const response: AxiosResponse<JobsRes> = await axios(
                `${getUrl()}/api/job?${queryParams}`
            );
            return response.data;
        } catch (e) {
            console.error('getJobs() failed to load', queryParams && isError(e) && e.message);
            if (isAxiosError(e)) {
                const jobError: JobError = new Error(e.message);
                jobError.info = isAxiosError(e) && e.response?.data;
                jobError.status = e.response?.status;
                throw jobError;
            }
        }
        return undefined;
    }

    public async getJob(jobId?: string) {
        try {
            const response: AxiosResponse<JobProps> = await axios(`${getUrl()}/api/job/${jobId}`);
            return response.data;
        } catch (e) {
            console.error('getJob() failed to load', jobId && isError(e) && e.message);
            if (isAxiosError(e)) {
                const jobError: JobError = new Error(e.message);
                jobError.info = isAxiosError(e) && e.response?.data;
                jobError.status = e.response?.status;
                throw jobError;
            } else {
                throw e;
            }
        }
    }

    public async getEducation(jobId: string, type: string, page?: number, searchTerm?: string): Promise<EducationsRes> {
        try {
            const response: AxiosResponse<EducationsRes> = await axios(
                `${getUrl()}/api/job/${jobId}/education/${type}?page=${page ? page : 1}${searchTerm && `&term=${searchTerm}`}`
            );
            return response.data;
        } catch (e) {
            console.error('getJob() failed to load', jobId && isError(e) && e.message);
            if (isAxiosError(e)) {
                const jobError: JobError = new Error(e.message);
                jobError.info = isAxiosError(e) && e.response?.data;
                jobError.status = e.response?.status;
                throw jobError;
            } else {
                throw e;
            }
        }
    }

    public async submitJobApplication(jobId?: string, applicationFormData?: ApplicationFormData) {
        try {
            const response: any = await axios.post(
                `${getUrl()}/api/job/${jobId}`,
                applicationFormData,
                {
                    timeout: 0,
                    validateStatus: (status) => status >= 200 && status < 500,
                }
            );
            return response.data;
        } catch (e) {
            console.error(
                'submitJobApplication() failed to submit',
                jobId && isError(e) && e.message
            );
            if (isAxiosError(e)) {
                const jobError: JobError = new Error(e.message);
                jobError.info = isAxiosError(e) && e.response?.data;
                jobError.status = e.response?.status;
                throw jobError;
            } else {
                throw e;
            }
        }
    }
}

export const jobFetcher = new JobFetch();
